import { Component, type ErrorInfo, type ReactNode } from 'react';

// Last-resort fallback. Automatic recovery from stale-deploy chunk 404s now lives
// in the lazy wrapper (src/lib/lazyRetry.ts, T67): it does the ONE guarded reload
// during the import, before any error reaches this boundary. So by the time an
// error lands here it's either a chunk that STILL failed after that single reload
// (a genuinely-broken deploy) or a real render error — in both cases the right
// move is to show this friendly bilingual page with a working reload button, NOT
// to auto-reload again (which would be a second reload for one failure).

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info?.componentStack);
  }

  private handleReload = () => {
    // A user-initiated refresh: reload once. If the deploy is now healthy the
    // fresh chunk loads and lazyRetry disarms its guard; if it's still broken we
    // land back here (no extra automatic reload).
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-teal-50 text-teal-600">
          <i className="ri-refresh-line text-2xl"></i>
        </div>
        <h1 className="text-xl font-semibold text-slate-900">
          Something went wrong
          <span className="mx-2 text-slate-300">·</span>
          页面加载出错
        </h1>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-500">
          The page didn&rsquo;t load correctly — this usually fixes itself with a refresh.
          <br />
          页面未能正确加载,刷新一下通常即可解决。
        </p>
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={this.handleReload}
            className="rounded-lg bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-teal-700"
          >
            Reload · 刷新
          </button>
          <a
            href="/"
            className="rounded-lg bg-slate-100 px-5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
          >
            Home · 首页
          </a>
        </div>
      </div>
    );
  }
}
